// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  CircularProgress,
  DialogActions,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material'
import { FC, useContext, useEffect } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../../../common/Alert'
import { CustomController } from '../../../common/CustomController'
import { CustomModal } from '../../../common/CustomModal'
// others
import {
  CreateExternalUser,
  ExternalUserStatus,
  useCreateExternalUserMutation,
  useGetNextClientNumberQuery
} from '../../../../../generated'
import { CANCEL, GRAPHQL_QUERY_POLICY, US_STATES } from '../../../../constants'
import { AppContext, CaseContext } from '../../../../context'
import { AddNewExternalUserProps } from '../../../../interfaceTypes'
import { returnErrorMessage } from '../../../../utils'
import { addNewClientValidationSchema } from '../../../../validationSchema'
import { InputFormFields } from '../../../externalUsers/InputFormFields'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ActionType } from '../../../../context/AppContextReducer'
import CheckboxController from '../../../common/CheckboxController'

export const AddNewClient: FC<AddNewExternalUserProps> = ({
  setNewExternalUser,
  refetch,
  customOpen,
  handleCustomClose,
  isConsultMatter
}) => {
  const { clientRoleId } = useContext(CaseContext)
  const { isOpen: isNewClientOpen, dispatch: appDispatch } = useContext(AppContext)
  const defaultState = US_STATES.filter(state => state.name === 'New Jersey')

  const { data: recentClient, refetch: refetchRecentClient } = useGetNextClientNumberQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: {
      isConsultClient: !!isConsultMatter
    }
  })

  const methods = useForm<CreateExternalUser>({
    mode: 'all',
    resolver: yupResolver(addNewClientValidationSchema),

    defaultValues: {
      firstName: '',
      lastName: '',
      businessName: '',
      status: ExternalUserStatus.Active,
      externalUserRoleId: clientRoleId,
      email: '',
      clientNumber: '',
      contractBalance: '0',
      residualBalance: '0',
      trustBalance: '0',
      arrearsBalance: '0',
      lastPayDate: '',
      lastPayAmount: '0',
      dateOfBirth: '',
      addresses: [
        {
          address1: '',
          address2: '',
          country: 'USA',
          county: '',
          externalUserId: '',
          state: defaultState[0].name
        }
      ]
    }
  })

  function generateClientNumber() {
    const clientNo = recentClient?.getNextClientNumber.clientNo

    setValue('clientNumber', `${clientNo}`)
    return `${clientNo}`
  }

  const [createExternalUserMutation, { loading: creteExternalUserLoader }] = useCreateExternalUserMutation({
    onError({ message }) {
      returnErrorMessage(message)
    },

    onCompleted(data) {
      if (data) {
        const {
          createExternalUser: { response, externalUser }
        } = data || {}

        if (response) {
          const { status } = response

          if (status === 200 && externalUser) {
            Alert.success('External User Created Successfully')
            setNewExternalUser && setNewExternalUser(externalUser)

            if (isNewClientOpen || customOpen) {
              handleClose()
              refetch && refetch()
            }

            setValue('externalUserRoleId', '')
            refetchRecentClient()
          }
        }
      }
    }
  })

  const handleClose = () => {
    if (handleCustomClose) handleCustomClose()
    else {
      appDispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isNewClientOpen })
      appDispatch({ type: ActionType.SET_IS_EDIT, isEdit: false })
    }
    reset()
  }

  const { handleSubmit, control, reset, setValue, watch } = methods
  const onSubmit: SubmitHandler<CreateExternalUser> = async (data: CreateExternalUser) => {
    const { firstName, lastName, phoneNumber, homePhone, personalPhone, dateOfBirth, ...rest } = data
    await createExternalUserMutation({
      variables: {
        createExternalUserInput: {
          ...rest,
          dateOfBirth: !!dateOfBirth ? dateOfBirth : undefined,
          lastPayDate: undefined,
          firstName: firstName?.trim() || undefined,
          lastName: lastName?.trim() || undefined,
          status: ExternalUserStatus.Active,
          externalUserRoleId: clientRoleId,
          phoneNumber: !!phoneNumber ? phoneNumber : undefined,
          homePhone: !!homePhone ? homePhone : undefined,
          personalPhone: !!personalPhone ? personalPhone : undefined,
          isConsultClient: !!isConsultMatter
        }
      }
    })
  }

  useEffect(() => {
    clientRoleId && setValue('externalUserRoleId', clientRoleId)
    // eslint-disable-next-line
  }, [clientRoleId, isNewClientOpen])

  const newClientNo = generateClientNumber()
  const title = isConsultMatter ? 'Add New Consult Client' : 'Add New Client'

  return (
    <CustomModal title={title} isOpen={customOpen ?? !!isNewClientOpen} handleClose={handleClose} maxWidth="lg">
      <FormProvider {...methods}>
        <form
          onSubmit={event => {
            event.stopPropagation()
            return handleSubmit(onSubmit)(event)
          }}
        >
          <Grid container spacing={2} rowSpacing={2}>
            <Grid item md={12} sm={12} xs={12}>
              <Typography variant="h6">General</Typography>
            </Grid>

            <InputFormFields isClient />

            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="socialSecurityNumber"
                controllerLabel="Social Security#"
                fieldType="text"
              />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="driverLicenseNumber"
                controllerLabel="Driver License#"
                fieldType="text"
                upperCase
              />
            </Grid>

            <Grid item md={12} sm={12} xs={12}>
              <CheckboxController
                name="isInternational"
                label={'International'}
                customOnChange={() => setValue('addresses.0.country', 'USA')}
              />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].address1"
                controllerLabel="Address line 1"
                fieldType="text"
              />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].address2"
                controllerLabel="Address line 2"
                fieldType="text"
              />
            </Grid>

            <Grid item md={4} sm={12} xs={12}>
              <CustomController controllerName="addresses[0].city" controllerLabel="City" fieldType="text" />
            </Grid>

            {!watch('isInternational') && (
              <Grid item md={4} sm={12} xs={12}>
                <FormControl variant="outlined" fullWidth sx={{ marginTop: 1 }}>
                  <InputLabel id="address-state">State</InputLabel>

                  <Controller
                    name="addresses.0.state"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="address-state"
                        label="State"
                        fullWidth
                        MenuProps={{ style: { maxHeight: 300 } }}
                      >
                        {US_STATES.map((item, index) => {
                          const { name } = item || {}
                          return (
                            <MenuItem value={name} key={`${item}-${index}`}>
                              {name}
                            </MenuItem>
                          )
                        })}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
            )}

            <Grid item md={watch('isInternational') ? 4 : 2} sm={12} xs={12}>
              <CustomController controllerName="addresses.0.zipCode" controllerLabel="Zip Code" fieldType="text" />
            </Grid>

            <Grid item md={watch('isInternational') ? 4 : 2} sm={12} xs={12}>
              <CustomController controllerName="addresses[0].country" controllerLabel="Country" fieldType="text" />
            </Grid>
          </Grid>

          <Grid container spacing={2} rowSpacing={2.5}>
            <Grid item md={12} sm={12} xs={12} mt={2}>
              <Typography variant="h6">Financial </Typography>
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="clientNumber"
                controllerLabel="Client Number"
                fieldType="text"
                defaultValue={newClientNo}
                isDisabled
                autoFocus
              />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <FormControl variant="outlined" fullWidth sx={{ marginTop: 1 }}>
                <Controller
                  name="lastPayDate"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error, invalid } }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        disablePast
                        label="Last Pay Date"
                        value={value}
                        onChange={value => {
                          onChange(value)
                        }}
                        readOnly
                        renderInput={params => (
                          <TextField
                            {...params}
                            error={invalid}
                            helperText={invalid ? error?.message : null}
                            fullWidth
                          />
                        )}
                      />
                    </LocalizationProvider>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="contractBalance"
                controllerLabel="Contract Balance"
                fieldType="text"
                isDisabled
              />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="residualBalance" controllerLabel="Residual Balance" fieldType="text" />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="trustBalance" controllerLabel="Trust Balance" fieldType="text" />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="lastPayAmount" controllerLabel="Last Pay Amount" fieldType="text" />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="arrearsBalance"
                controllerLabel="AR Balance"
                fieldType="text"
                dollarAdornment
              />
            </Grid>
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} variant="outlined" aria-label={CANCEL}>
              {CANCEL}
            </Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={creteExternalUserLoader}
              aria-label="add new client"
              endIcon={creteExternalUserLoader && <CircularProgress size={20} color="inherit" />}
            >
              Add
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}
